import { Injectable } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router, NavigationEnd } from '@angular/router';

const keyName = 'FilterState';

@Injectable({
  providedIn: 'root',
})
export class FilterService<F, T> {
  private key!: string;

  private refreshOnReload: boolean = false;

  constructor(private router: Router) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.removeFiltersFromSessionStorage();
      }
    });
    // ページリフレッシュ時にフィルタを削除する
    window.addEventListener('beforeunload', () => {
      if (this.refreshOnReload) {
        this.removeFiltersFromSessionStorage();
      }
    });
  }
  /**
   * フィルタ条件をセッションストレージに保存する
   * @param pageId - 画面名
   * @param newFilters - 新しいフィルタオブジェクト
   */
  setFiltersToSessionStorage(newFilters: F): void {
    const existingFilters = this.getFiltersFromSessionStorage();
    const mergedFilters = { ...existingFilters, ...newFilters };
    sessionStorage.setItem(this.key, JSON.stringify(mergedFilters));
  }

  /**
   * セッションストレージからフィルタ条件を取得する
   * @param screenName - 画面名
   * @returns フィルタオブジェクト
   */
  getFiltersFromSessionStorage(clearFilters?: boolean): F {
    if (clearFilters) {
      return {} as F;
    }
    const filters = sessionStorage.getItem(this.key);
    return filters ? JSON.parse(filters) : {};
  }

  // セッションストレージからフィルタ条件を削除する
  removeFiltersFromSessionStorage(): void {
    // 全てのキーを取得
    const keys = Object.keys(sessionStorage);

    // 部分一致するキーを探して削除
    keys.forEach((key) => {
      if (key.includes(keyName)) {
        sessionStorage.removeItem(key);
      }
    });
  }

  /**
   * セッションストレージ用のキー を設定する
   * @param pageId - 画面名
   */
  setSessionStorageKey(pageId: string, refreshFlag?: boolean): void {
    if (refreshFlag) {
      this.refreshOnReload = refreshFlag;
    }
    this.key = keyName + pageId;
  }

  /**
   * ソートを設定する
   * @param dataSource
   * @param sort
   * @param sortState
   */
  setSort(dataSource: MatTableDataSource<T>, sort: MatSort, sortState: Sort) {
    setTimeout(() => {
      dataSource.sort = sort;
      const sortData: Sort = {
        active: sortState.active,
        direction: sortState.direction,
      };
      sort.active = sortState.active;
      sort.direction = sortState.direction;
      sort.sortChange.emit(sortData);
    }, 0);
  }

  /**
   * ソートとページングの変更を検知し、ストレージに設定する
   * @param sort
   * @param paginator
   */
  monitorSortAndPagination(sort: MatSort, paginator?: MatPaginator) {
    if (sort) {
      sort.sortChange.subscribe((sortState) => {
        const filter: Partial<F> = {
          sortState: sortState,
        } as unknown as Partial<F>;
        this.setFiltersToSessionStorage(filter as F);
      });
    }
    if (paginator) {
      paginator.page.subscribe((pageEvent) => {
        const filter: Partial<F> = {
          pageIndex: pageEvent.pageIndex,
        } as unknown as Partial<F>;
        this.setFiltersToSessionStorage(filter as F);
      });
    }
  }

  /**
   * 有効なページインデックスを取得する
   * @param pageIndex - 入力されたページインデックス
   * @param pageCount - ページ数
   * @returns 範囲内のページインデックス
   */
  getValidPageIndex(paginator: MatPaginator, pageIndex: number): number {
    const totalPages = Math.ceil(paginator.length / paginator.pageSize);
    if (pageIndex < 0) {
      pageIndex = 0;
    } else if (pageIndex >= totalPages) {
      pageIndex = totalPages - 1;
    }
    return pageIndex;
  }
}
